import React, { useEffect } from 'react';

const QuantumTlsTransformer = () => {
  useEffect(() => {
    document.title = 'Quantum TLS Transformer, Practical post-quantum transport security';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Quantum TLS Transformer: a lightweight proxy-like component that enables quantum-safe or hybrid TLS negotiation when the destination supports it, with graceful fallback to standard TLS. Deploy on an existing server with minimal disruption.');
    }
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Hero */}
      <header className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Quantum TLS Transformer</h1>
        <p className="text-lg text-gray-700 mb-6">A practical way to use quantum-safe transport security when the destination supports it, without adding heavy infrastructure.</p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            className="inline-block bg-blue-600 text-white px-5 py-3 rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            href="mailto:contact@quantumsafeaudit.com"
            role="button"
          >
            Request a demo
          </a>
        </div>
          </header>

          {/* Technical brief anchor (short summary and links) */}
          <section id="technical-brief" className="mb-10 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-3">Technical brief</h2>
            <p className="mb-3 text-gray-700">A short technical summary and implementation notes are available for engineering teams. For architecture details, example deployments and configuration guidance, consult the repository or request a technical discussion.</p>
              </section>

      {/* Problem section */}
      <section className="mb-10 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-3">Why this matters now</h2>
        <p className="mb-3">Encrypted traffic sent today may be recorded and stored by adversaries with the intent to break it later when more powerful quantum systems exist. For organisations that handle long-lived or sensitive data, this "harvest now, decrypt later" risk is real, and waiting for an all-or-nothing migration can leave data exposed.</p>
        <p className="mb-3">Quantum TLS Transformer provides a pragmatic, incremental path: where destination servers support quantum-safe or hybrid TLS, connections use those stronger options; where they do not, the component falls back to standard TLS, preserving compatibility and uptime.</p>
      </section>

      {/* How it works */}
      <section className="mb-10 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-3">How it works</h2>
        <p className="mb-3">Quantum TLS Transformer runs as a lightweight proxy-like layer that you can place in front of, or alongside, existing services. It inspects outbound TLS negotiation and, when the remote endpoint advertises support for post-quantum or hybrid key exchanges, it negotiates those stronger options on behalf of your application.</p>
        <p className="mb-3">If the destination does not support quantum-safe negotiation, the Transformer gracefully falls back to the regular TLS negotiation your stack already uses. This behaviour makes adoption incremental and safe: there is no requirement to change client application code, and no all-or-nothing cutover.</p>
        <ul className="list-disc pl-5 mt-3">
          <li>Inline or sidecar deployment: runs on the same host or in the same network path.</li>
          <li>Negotiates quantum-safe or hybrid TLS when the destination advertises support.</li>
          <li>Graceful fallback to standard TLS to ensure compatibility.</li>
        </ul>
      </section>

      {/* Benefits */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-6 text-center">Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Practical future-readiness</h3>
            <p className="text-sm text-gray-700">Reduce exposure to future quantum-enabled decryption by using quantum-safe/hybrid TLS where available, without waiting for universal support.</p>
          </article>

          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Minimal operational impact</h3>
            <p className="text-sm text-gray-700">Deploy on existing servers or as an inline proxy — no dedicated server tier or heavy platform required.</p>
          </article>

          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Incremental adoption</h3>
            <p className="text-sm text-gray-700">Works alongside current TLS stacks and offers phased rollout: enable it for select services, then expand as endpoints gain support.</p>
          </article>

          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Compatibility-first</h3>
            <p className="text-sm text-gray-700">Transparent fallback to standard TLS ensures connections continue to work even if the remote side lacks post-quantum support.</p>
          </article>

          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Low operational overhead</h3>
            <p className="text-sm text-gray-700">A lightweight component with simple deployment patterns reduces adoption cost and operational complexity.</p>
          </article>

          <article className="p-5 border rounded-lg shadow-sm bg-white">
            <h3 className="font-semibold mb-2">Safe experimentation</h3>
            <p className="text-sm text-gray-700">Run trials against a subset of services to assess benefits before wide rollout — no application rewrites required.</p>
          </article>
        </div>
      </section>

      {/* Lightweight deployment */}
      <section className="mb-10 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-3">Lightweight deployment, no extra infrastructure</h2>
        <p className="mb-3">Quantum TLS Transformer is intentionally small and flexible. It is designed to run on an existing server or as a sidecar; you do not need a separate appliance, a new server tier, or a heavy platform to start using it.</p>
        <p className="mb-3">Because it integrates into the existing request path, teams can introduce it with minimal operational disruption: introduce it for test workloads, validate behaviour, and then expand to production workloads at your pace.</p>
        <ul className="list-disc pl-5 mt-3">
          <li>No dedicated new servers required, run on current infrastructure.</li>
          <li>Place it in front of or alongside services with simple routing changes.</li>
          <li>Suitable for phased rollout and pilot programmes.</li>
        </ul>
      </section>

      {/* Dutch explainer section */}
      <section className="mb-10 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-3">Bekijk de uitleg (Nederlands)</h2>
        <p className="mb-4">Onderstaande video is een Nederlandstalige uitleg van het concept. Het helpt om te begrijpen waarom quantum-veilige TLS relevant is en hoe een pragmatische introductie kan werken.</p>
        <div className="md:flex md:gap-6">
          <div className="md:flex-1">
            <div className="relative" style={{ paddingTop: '56.25%' }}>
              <iframe
                title="Quantum TLS Transformer - Dutch explainer"
                className="absolute top-0 left-0 w-full h-full rounded shadow-sm"
                src="https://www.youtube.com/embed/CZTvqN10pmk"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <div className="md:w-1/3 mt-4 md:mt-0">
            <h3 className="font-semibold mb-2">Korte samenvatting</h3>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              <li>Nederlandstalige uitleg van het concept en de praktische relevantie.</li>
              <li>Toont waarom een pragmatische, incrementele aanpak helpt.</li>
              <li><a href="https://www.youtube.com/watch?v=CZTvqN10pmk" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Bekijk de uitleg</a></li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-10 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-4">Frequently asked questions</h2>
        <div className="space-y-4">
          <details className="p-4 border rounded">
            <summary className="font-semibold">What is Quantum TLS Transformer?</summary>
            <p className="mt-2 text-sm text-gray-700">A lightweight proxy-like component that negotiates quantum-safe or hybrid TLS when the destination supports it, and falls back to standard TLS otherwise. It is intended to provide a pragmatic, incremental way to improve transport security.</p>
          </details>

          <details className="p-4 border rounded">
            <summary className="font-semibold">Does it require new infrastructure?</summary>
            <p className="mt-2 text-sm text-gray-700">No. It is designed to run on existing servers or as a sidecar alongside your services. You do not need a dedicated appliance or a separate server tier.</p>
          </details>

          <details className="p-4 border rounded">
            <summary className="font-semibold">Does it only work if the destination supports post-quantum or hybrid TLS?</summary>
            <p className="mt-2 text-sm text-gray-700">Yes — Quantum TLS Transformer leverages quantum-safe or hybrid TLS when the remote endpoint advertises support. If the destination does not support those options, the Transformer performs a standard TLS negotiation to maintain compatibility.</p>
          </details>

          <details className="p-4 border rounded">
            <summary className="font-semibold">Do I need to change my application code?</summary>
            <p className="mt-2 text-sm text-gray-700">In most cases, no. The component is intended to be transparent to application code and introduced with minimal configuration changes to routing or proxy rules.</p>
          </details>

          <details className="p-4 border rounded">
            <summary className="font-semibold">Is this an all-or-nothing migration?</summary>
            <p className="mt-2 text-sm text-gray-700">No. The Transformer supports phased adoption and experimentation. You can enable it for a subset of services or environments and expand as endpoints and tooling mature.</p>
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section id="contact" className="mb-16 text-center">
        <h2 className="text-2xl font-semibold mb-3">Ready to explore a practical path to quantum-ready transport?</h2>
        <p className="mb-4 text-gray-700">Contact us for a technical discussion, pilot deployment, or demo. We can help you evaluate minimal-impact rollout patterns tailored to your stack.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            className="inline-block bg-blue-600 text-white px-5 py-3 rounded shadow hover:bg-blue-700 focus:outline-none"
            href="mailto:contact@quantumsafeaudit.com"
          >
            Request a technical discussion
          </a>
        </div>
      </section>
    </div>
  );
};

export default QuantumTlsTransformer;
